import Spin from 'antd/lib/spin';
import * as React from 'react';
import { FetchableEntityType } from '../../entityType';
import { MehOutlined } from '@ant-design/icons';
import Result from 'antd/lib/result';
import { antGrey } from '../styles/globalStyles';
import Button from 'antd/lib/button';
import { selectIsReleaseFetched } from '../../selectors/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { tryFetchEntity } from '../../actions/entities';
import { ReleaseComponentProps } from '../../typescriptHelpers';
import { trackReleaseRouteEvent } from '../../actions/trackEvent';
import * as AnalyticEvents from '../../utils/analytics';
import { useError, useIsLoading } from '@/utils/hooks/helpers';

export const withRelease = <P extends object>(WrappedComponent: React.ComponentType<P & ReleaseComponentProps>) => {
    const ReleaseWrapper: React.FC<
        P & ReleaseComponentProps & { showLoader?: boolean; showError?: boolean; withTracking?: boolean }
    > = (props) => {
        const { orgId, appId, releaseId, showLoader = true, showError = true, withTracking = true } = props;
        const [displayError, setDisplayError] = React.useState(false);

        const dispatch = useDispatch();
        const isLoading = useIsLoading(FetchableEntityType.Release, releaseId);
        const errorFetching = useError(FetchableEntityType.Release, releaseId);
        const isReleaseFetched = useSelector((state: RootState) => selectIsReleaseFetched(state, releaseId));

        const fetchRelease = () => {
            setDisplayError(false);

            dispatch(tryFetchEntity(FetchableEntityType.Release, { orgId, appId, releaseId }, releaseId));
        };

        React.useEffect(() => {
            if (!isReleaseFetched) {
                fetchRelease();
            }
        }, [releaseId, isReleaseFetched]);

        React.useEffect(() => {
            if (withTracking && isReleaseFetched) {
                dispatch(
                    trackReleaseRouteEvent({
                        orgId: orgId,
                        appId: appId,
                        releaseId: releaseId,
                        eventName: AnalyticEvents.Event.ViewRelease,
                    }),
                );
            }
        }, [withTracking, releaseId, isReleaseFetched]);

        React.useEffect(() => {
            if (errorFetching) {
                setDisplayError(true);
            }
        }, [errorFetching]);

        return (
            <>
                {isLoading && showLoader && (
                    <div
                        style={{
                            display: 'flex',
                            flexGrow: 100,
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Spin size="small" />
                    </div>
                )}
                {displayError && showError && (
                    <div
                        style={{
                            display: 'flex',
                            flexGrow: 1,
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Result
                            icon={<MehOutlined style={{ color: antGrey.grey5 }} />}
                            subTitle="Error fetching release"
                            extra={<Button onClick={fetchRelease}>Try again</Button>}
                        />
                    </div>
                )}
                {isReleaseFetched && <WrappedComponent {...props} orgId={orgId} appId={appId} releaseId={releaseId} />}
            </>
        );
    };

    // Set display name for easier debugging
    const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    ReleaseWrapper.displayName = `withRelease(${wrappedComponentName})`;

    return ReleaseWrapper;
};
